import { Link } from 'react-router-dom';
import type { Topic } from '@/types/content';

function formatHeat(heat: number) {
  if (heat >= 10000) return `${(heat / 10000).toFixed(1)} 万`;
  return String(heat);
}

const tagStyle: Record<string, string> = {
  突发: 'bg-primary-100 text-primary-800',
  热议: 'bg-accent-100 text-accent-900',
  关注: 'bg-secondary-100 text-secondary-900',
  焦点: 'bg-primary-100 text-primary-800',
};

export default function TopicCard({
  topic,
  categoryName,
  layout = 'vertical',
}: {
  topic: Topic;
  categoryName?: string;
  layout?: 'vertical' | 'horizontal';
}) {
  const badgeClass = tagStyle[topic.tag] ?? 'bg-secondary-100 text-secondary-900';

  if (layout === 'horizontal') {
    return (
      <article className="group flex gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 md:p-4">
        <Link
          to={`/topic/${topic.slug}`}
          className="relative h-20 w-28 shrink-0 overflow-hidden rounded-md md:h-24 md:w-36"
        >
          <img
            src={topic.cover}
            alt={topic.coverAlt}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className={`rounded-full px-2 py-0.5 ${badgeClass}`}>{topic.tag}</span>
            {categoryName && <span className="text-foreground-500">{categoryName}</span>}
            <span className="text-foreground-500">{topic.timeAgo}</span>
          </div>
          <h3 className="mt-2 line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-950 md:text-base">
            <Link to={`/topic/${topic.slug}`} className="cursor-pointer transition-colors hover:text-primary-700">
              {topic.title}
            </Link>
          </h3>
          <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-sm">{topic.summary}</p>
          <div className="mt-auto flex flex-wrap items-center gap-3 pt-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-fire-line text-primary-600" />
              </span>
              {formatHeat(topic.heat)}
            </span>
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-eye-line" />
              </span>
              {topic.readCount}
            </span>
            <span className="flex items-center gap-1">
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-chat-3-line" />
              </span>
              {topic.commentCount}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300">
      <Link to={`/topic/${topic.slug}`} className="relative block h-44 w-full overflow-hidden md:h-48">
        <img
          src={topic.cover}
          alt={topic.coverAlt}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs ${badgeClass}`}>{topic.tag}</span>
      </Link>
      <div className="flex flex-1 flex-col p-4">
        <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          {categoryName && (
            <Link to={`/category/${topic.categorySlug}`} className="cursor-pointer text-primary-700 hover:text-primary-600">
              {categoryName}
            </Link>
          )}
          <span>{topic.timeAgo}</span>
        </div>
        <h3 className="mt-2 line-clamp-2 font-heading text-base font-bold leading-snug text-foreground-950">
          <Link to={`/topic/${topic.slug}`} className="cursor-pointer transition-colors hover:text-primary-700">
            {topic.title}
          </Link>
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{topic.summary}</p>
        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-background-200 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-fire-line text-primary-600" />
            </span>
            热度 {formatHeat(topic.heat)}
          </span>
          <span className="flex items-center gap-1">
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-chat-3-line" />
            </span>
            {topic.commentCount}
          </span>
        </div>
      </div>
    </article>
  );
}